<template>
  <div class="scroll-text-gradient" ref="container">
    <div class="text-container">
      <div class="gradient-text" :style="getTextStyles()">
        {{ text }}
      </div>
    </div>
  </div>
</template>

<script>
import { gsap, ScrollTrigger } from "@/utils/gsapConfig.js";

export default {
  name: "AnimationText",
  props: {
    // 文字数组
    text: {
      type: String,
      default: "1234567890",
    },
    // 初始颜色
    backgroundColor: {
      type: String,
      default: "rgba(0, 0, 0, 0.1)",
    },
    // 主题色（渐变色）
    themeColor: {
      type: Array,
      default: () => [],
    },
    // 结尾颜色
    endColor: {
      type: String,
      default: "#FFFFFF",
    },
    // 字体大小
    fontSize: {
      type: Number,
      default: 96,
    },
    // 字体粗细
    fontWeight: {
      type: Number,
      default: 600,
    },
    // 行高
    lineHeight: {
      type: [Number, String],
      default: 76.8,
    },
    // 触发开始位置
    startTrigger: {
      type: String,
      default: "top 80%",
    },
    // 触发结束位置
    endTrigger: {
      type: String,
      default: "top 20%",
    },
    // 元素可见占比达到多少才初始化动效 (0-1)
    activateRatio: {
      type: Number,
      default: 0.5,
    },
    // 延迟初始化（毫秒），可选防抖
    activateDelay: {
      type: Number,
      default: 0,
    },
    originLinerCssStyle: {
      type: String
    },
    gsapProgress: {
      type: Number,
      default: 0.7,
    }
  },

  data() {
    return {
      scrollProgress: 0,
      isAnimating: false,
      gradientTimeline: null,
      _skipNextEnter: false,
      hasAnimated: false,
      supportsBackgroundClip: false,
    };
  },

  computed: {
    textList() {
      return this.texts;
    },
  },

  mounted() {
    this.checkBackgroundClipSupport();
    this.lazyActivate();
  },
  methods: {
    checkBackgroundClipSupport() {
      // 检查浏览器是否支持 background-clip: text
      const testEl = document.createElement("div");
      testEl.style.cssText =
        "-webkit-background-clip: text; background-clip: text;";
      this.supportsBackgroundClip =
        testEl.style.webkitBackgroundClip === "text" ||
        testEl.style.backgroundClip === "text";
    },
    lazyActivate() {
      const container = this.$refs.container;
      if (!container) return;
      if (typeof IntersectionObserver === "undefined") {
        this.setupScrollAnimation();
        return;
      }
      const thresholds = [];
      for (let i = 0; i <= 1; i += 0.05)
        thresholds.push(parseFloat(i.toFixed(2)));
      this._io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.intersectionRatio >= this.activateRatio) {
            setTimeout(() => {
              this.setupScrollAnimation();
            }, this.activateDelay);
            this._io.disconnect();
            this._io = null;
          }
        },
        { threshold: thresholds }
      );
      this._io.observe(container);
    },

    setupScrollAnimation() {
      if (this._stCreated) return;
      const container = this.$refs.container;
      this._stCreated = true;

      ScrollTrigger.create({
        // markers: true,
        trigger: container,
        start: this.startTrigger,
        end: this.endTrigger,
        onEnter: (self) => {
          if (self.direction === 1 && !this.hasAnimated) {
            this.startGradientAnimation();
          } else if (self.direction === -1 && !this.hasAnimated) {
            this.setToFinalState();
          }
        },
        onEnterBack: () => {
          this.setToFinalState();
          this.hasAnimated = true;
        },
        onLeaveBack: () => {
          if (!this.hasAnimated) {
            this.resetAnimation();
          }
        },
      });
    },

    setToFinalState() {
      this.scrollProgress = this.gsapProgress;
      this.isAnimating = false;
      if (this.gradientTimeline) {
        this.gradientTimeline.kill();
        this.gradientTimeline = null;
      }
      this.$forceUpdate();
    },

    startGradientAnimation() {
      if (this.isAnimating) return;
      this.isAnimating = true;

      const duration = 2.0;
      const ease = "power2.out";

      this.gradientTimeline = gsap.timeline({
        onUpdate: () => {
          this.$emit("progress", {
            progress: this.scrollProgress,
            direction: 1,
          });
        },
        onComplete: () => {
          this.isAnimating = false;
          this.hasAnimated = true;
        },
      });

      this.gradientTimeline.to(this, { scrollProgress: this.gsapProgress, duration, ease });
    },

    resetAnimation() {
      this.scrollProgress = 0;
      this.isAnimating = false;

      if (this.gradientTimeline) {
        this.gradientTimeline.kill();
      }
    },

    getTextStyles() {
      const styles = {
        "--font-size-value": this.fontSize,
        "--font-weight-value": this.fontWeight,
        "--line-height-value": this.lineHeight,
      };

      if (this.supportsBackgroundClip) {
        // 现代浏览器：使用背景渐变
        styles.background = this.getTextBackground();
        styles.webkitTextFillColor = "transparent";
        styles.color = "transparent";
      } else {
        // 兼容模式：使用普通颜色
        styles.background = this.originLinerCssStyle
      }

      return styles;
    },

    getTextBackground() {
      // 计算每个文字的进度范围
      const textStartProgress = 0;
      const textEndProgress = 1;

      // 计算当前文字的局部进度 (0-1)
      let localProgress = 0;
      if (this.scrollProgress >= textStartProgress) {
        if (this.scrollProgress <= textEndProgress) {
          localProgress = this.scrollProgress - textStartProgress;
        } else {
          localProgress = 1; // 完全完成
        }
      }

      const pos1 = 100 + (-100 - 100) * localProgress;
      const pos2 = 144 + (-60 - 140) * localProgress;
      const pos3 = 158 + (-40 - 160) * localProgress;
      const pos4 = 200 + (0 - 200) * localProgress;

      const themeColor = this.themeColor;
      if (this.themeColor.length === 3) {
        return `linear-gradient(270deg, ${this.backgroundColor} ${pos1}%, ${themeColor[0]} ${pos2}%, ${themeColor[1]} ${pos3}%, ${this.endColor} ${pos4}%) text`;
      } else if (this.themeColor.length === 4) {
        return `linear-gradient(270deg, ${this.backgroundColor} ${pos1}%, ${themeColor[0]} ${pos2}%, ${themeColor[1]} ${pos3}%, ${themeColor[2]} ${pos4}%, ${this.endColor} 100%) text`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-text-gradient {
  display: flex;
  justify-content: center;
}

.text-container {
  display: flex;
  flex-direction: column;
  // gap: 40px;
  align-items: center;
}

.gradient-text {
  font-weight: var(--font-weight-value);
  text-align: center;
  line-height: calc(var(--line-height-value) * #{$px1920vw}vw);
  letter-spacing: normal;
  font-size: calc(var(--font-size-value) * #{$px1920vw}vw);
  font-family: "vivoSansSCVF600";

  /* 现代浏览器的渐变文字支持 */
  background-clip: text;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent;
  user-select: none;
}

.isPad {
  .gradient-text {
    font-size: calc(var(--font-size-value) * #{$px676vw}vw);
    line-height: normal;
  }
}

.isMobile {
  .gradient-text {
    font-size: calc(var(--font-size-value) * #{$px360vw}vw);
    line-height: normal;
  }
}
</style>
